import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import { Trash, Pencil } from "react-bootstrap-icons";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createConfirmFeedback } from "../../../../context/ui-feedback/action";
import { CellActions } from "../../../common/cell-actions/cell-actions";
import { Loading } from "../../../common/loading/loading";
import {
  formatDate,
  getNextDay,
} from "../../../../utils/date-utils/date-utils";
import {
  isServiceWorksheetDeleteability,
  checkServiceWorksheetUpdateability,
} from "../../../../utils/service-worksheet-utils/service-worksheet-utils";

export const ServiceWorksheetTable = ({
  rowNumberInit,
  serviceWorksheets,
  hasError,
  handleServiceWorksheetDelete,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const TableBody = ({ serviceWorksheets, rowNumberInit }) => {
    if (serviceWorksheets.length === 0) {
      return (
        <tr>
          <td colSpan={7}>
            <span className="text-danger">
              <b>
                <FormattedMessage id="data.serviceworksheet.warning.notfoundserviceworksheet" />
              </b>
            </span>
          </td>
        </tr>
      );
    }

    const getFormatedSWDate = (date) => {
      if (date.type === "PERIOD" && date.date1Time && date.date2Time) {
        return `${formatDate(new Date(date.date1Time), "YYYY.MM.DD. hh:mm")} - ${formatDate(new Date(date.date2Time), "YYYY.MM.DD. hh:mm")}`;
      } else if (date.type === "FULL_DAY" && date.date1Time) {
        return `${formatDate(new Date(date.date1Time), "YYYY.MM.DD.")}`;
      } else {
        return "-";
      }
    };

    const setDeleteConfirm = (serviceWorksheet) => {
      createConfirmFeedback(
        uiFeedbackDispatch,
        intl.formatMessage({
          id: "page.serviceworksheets.label.deleteserviceworksheet.title",
        }),
        intl.formatMessage(
          {
            id: "page.serviceworksheets.label.deleteserviceworksheet.question",
          },
          { salesOrderNumber: serviceWorksheet?.salesOrder?.number },
        ),
        () => {
          handleServiceWorksheetDelete(serviceWorksheet);
        },
      );
    };

    return serviceWorksheets.map((serviceWorksheet, index) => {
      const buttons = [];
      if (isServiceWorksheetDeleteability(serviceWorksheet)) {
        buttons.push({
          variant: "secondary",
          title: intl.formatMessage({
            id: "page.serviceworksheets.button.deleteserviceworksheet",
          }),
          icon: <Trash />,
          action: () => {
            setDeleteConfirm(serviceWorksheet);
          },
        });
      }
      if (
        !checkServiceWorksheetUpdateability(serviceWorksheet).error ||
        serviceWorksheet.status === "ALLOCATED"
      ) {
        buttons.push({
          variant: "secondary",
          title: intl.formatMessage({
            id: "page.serviceworksheets.button.editserviceworksheets",
          }),
          icon: <Pencil />,
          action: () => {
            navigate(`/editserviceworksheet/${serviceWorksheet.id}`);
          },
        });
      }
      const endDate = serviceWorksheet.date.date2Time
        ? new Date(serviceWorksheet.date.date2Time)
        : new Date(serviceWorksheet.date.date1Time);
      if (
        serviceWorksheet.status === "ALLOCATED" &&
        getNextDay(endDate) <= new Date()
      ) {
        buttons.push({
          variant: "secondary",
          title: intl.formatMessage({
            id: "page.serviceworksheets.button.deleteserviceworksheet",
          }),
          icon: <Trash />,
          action: () => {
            setDeleteConfirm(serviceWorksheet);
          },
        });
      }

      return (
        <tr key={serviceWorksheet.id}>
          <td className="col-rownumber">{rowNumberInit + index + 1}.</td>
          <td className="col-salesordernumber">
            <NavLink to={`/editserviceworksheet/${serviceWorksheet.id}`}>
              {serviceWorksheet.onSiteWorksheet ? (
                <FormattedMessage
                  id={"page.serviceworksheets.label.onsiteservice"}
                />
              ) : (
                serviceWorksheet.salesOrder.number
              )}
            </NavLink>
          </td>
          <td className="col-date">
            {getFormatedSWDate(serviceWorksheet.date)}
          </td>
          <td className="col-status">
            <FormattedMessage
              id={`data.serviceworksheet.data.status.value.${serviceWorksheet.status}`}
            />
            {serviceWorksheet.status === "ALLOCATED" &&
            serviceWorksheet.mobilSync ? (
              <div>
                (
                <FormattedMessage
                  id={"data.serviceworksheet.data.status.sync"}
                />
                )
              </div>
            ) : null}
          </td>
          <td className="col-partner">
            {serviceWorksheet.salesOrder.customer.name}
          </td>
          <td className="col-actions">
            <CellActions buttons={buttons} />
          </td>
        </tr>
      );
    });
  };

  const LoadingBody = ({ hasError }) => (
    <tr>
      <td colSpan={7}>
        {hasError ? (
          <span className="text-danger">
            <FormattedMessage id="data.serviceworksheet.exception.listServiceWorksheet" />
          </span>
        ) : (
          <Loading />
        )}
      </td>
    </tr>
  );

  return (
    <div className="serviceworksheet-table-component table-responsive">
      <Table striped bordered>
        <thead>
          <tr>
            <th className="col-rownumber">#</th>
            <th className="col-salesordernumber">
              <FormattedMessage id="data.serviceworksheet.data.salesordernumber" />
            </th>
            <th className="col-date">
              <FormattedMessage id="data.serviceworksheet.data.date" />
            </th>
            <th className="col-status">
              <FormattedMessage id="data.serviceworksheet.data.status.label" />
            </th>
            <th className="col-partner">
              <FormattedMessage id="data.serviceworksheet.data.partner" />
            </th>
            <th className="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {serviceWorksheets ? (
            <TableBody
              serviceWorksheets={serviceWorksheets}
              rowNumberInit={rowNumberInit}
            />
          ) : (
            <LoadingBody hasError={hasError} />
          )}
        </tbody>
      </Table>
    </div>
  );
};

ServiceWorksheetTable.propTypes = {
  rowNumberInit: PropTypes.number.isRequired,
  serviceWorksheets: PropTypes.array,
  hasError: PropTypes.bool.isRequired,
  handleServiceWorksheetDelete: PropTypes.func.isRequired,
};
